import React, { useEffect, useState } from 'react'

import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'

import LoadingSection from 'components_new/molecules/LoadingSection'
import StatusBox from 'components_new/molecules/StatusBox'

import LinearProgress from 'components_new/atoms/LinearProgress'
import Text from 'components_new/atoms/Text'

import { formatClock } from 'utils/duration'

const TICK_INTERVAL_MS = 1000
// Used when the analysis type has no runtime history to estimate from.
const DEFAULT_RUNTIME_SECONDS = 60

interface RunningAnalysisProps {
  id: null | string

  /** On the analysis' own page rather than in the dialog: one can leave. */
  inline?: boolean
  /** When the running generation was created, as an ISO timestamp. */
  startedAt: null | string
}

const RunningAnalysis = (props: RunningAnalysisProps) => {
  const { id, inline, startedAt } = props

  const { analysisType } = useAnalysisType(id)
  const { t } = useTranslation()

  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), TICK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [])

  const aiAnalysisTitles = [
    t('analysis_types', 'ai_step_1'),
    t('analysis_types', 'ai_step_2'),
    t('analysis_types', 'ai_step_3'),
    t('analysis_types', 'ai_step_4')
  ]

  if (!id || !analysisType) return null

  // Clamped so a client clock behind the server never shows negative time.
  const elapsed = startedAt
    ? Math.max(0, (now - Date.parse(startedAt)) / 1000)
    : 0
  const estimate =
    analysisType.average_runtime_seconds || DEFAULT_RUNTIME_SECONDS
  const remaining = estimate - elapsed

  const getRemainingLabel = () => {
    if (remaining <= 0) return t('analysis_types', 'running_overdue')
    if (remaining <= 60)
      return t('analysis_types', 'running_less_than_minute_left')

    return t('analysis_types', 'running_minutes_left', false, {
      minutes: String(Math.ceil(remaining / 60))
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <LoadingSection
        background={true}
        description={aiAnalysisTitles}
        direction="column"
        sx={{ minHeight: 200 }}
        title={analysisType.name}
        variant="logo"
      />

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text variant="caption">
              <Icon
                name="TimerOutlined"
                sx={{ fontSize: '1em', verticalAlign: 'middle' }}
              />{' '}
              {formatClock(elapsed)}
            </Text>
          </Box>

          <Box>
            <Text variant="caption">{getRemainingLabel()}</Text>
          </Box>
        </Box>
        {remaining > 0 ? (
          <LinearProgress
            color="brand"
            value={(elapsed / estimate) * 100}
            variant="determinate"
          />
        ) : (
          <LinearProgress color="brand" variant="indeterminate" />
        )}
      </Box>

      <StatusBox
        color="brand"
        severity="info"
        title={
          inline
            ? t('analyses', 'rerun_leave_title')
            : t('analysis_types', 'running_can_close_title')
        }
      >
        {inline
          ? t('analyses', 'rerun_leave_body')
          : t('analysis_types', 'running_can_close_body')}
      </StatusBox>
    </Box>
  )
}

export default RunningAnalysis
